import React, { useMemo } from "react";
import type { ProductItem } from "../services/api";
import type { CourseProgressSummary } from "../appTypes";
import ContentCard from "../components/ContentCard";
import "./CoursesPage.css";

interface CoursesPageProps {
  courses: ProductItem[];
  progressList: CourseProgressSummary[];
  loadingData: boolean;
  pageError: string;
}

// Categoria pago × grátis. Heurística até o backend expor um campo explícito
// (ex.: product.isPaid ou category). Ver INTEGRATION.md §1.
const isPaidCourse = (c: ProductItem) => !!c.checkoutUrl || c.isOffer;

const CoursesPage: React.FC<CoursesPageProps> = ({ courses, progressList, loadingData, pageError }) => {
  const progressMap = useMemo(
    () => new Map(progressList.map((p) => [p.courseId, p])),
    [progressList],
  );
  const sorted = useMemo(
    () => courses.slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [courses],
  );
  const free = sorted.filter((c) => !isPaidCourse(c));
  const paid = sorted.filter(isPaidCourse);

  const renderCard = (course: ProductItem, index: number) => {
    const prog = progressMap.get(course.id);
    const locked = !course.hasAccess;
    const meta = locked
      ? "Bloqueado"
      : prog && prog.totalLessons
        ? `${prog.progressPercent}% · ${prog.watchedLessons}/${prog.totalLessons} aulas`
        : "Pronto para começar";
    return (
      <ContentCard
        key={course.id}
        title={course.title}
        description={course.description}
        cover={course.banner}
        badge={locked ? "Premium" : "Curso"}
        locked={locked}
        progressPercent={!locked && prog ? prog.progressPercent : undefined}
        meta={meta}
        actionLabel={locked ? "Desbloquear" : "Assistir"}
        href={locked ? course.checkoutUrl || undefined : `#/curso/${course.id}`}
        external={locked && !!course.checkoutUrl}
        toneIndex={index}
      />
    );
  };

  const renderSkeletons = (n: number) =>
    Array.from({ length: n }).map((_, i) => (
      <div className="cc cc-skeleton" key={i} aria-hidden="true">
        <div className="cc-thumb" />
        <div className="cc-body">
          <span className="sk sk-1" />
          <span className="sk sk-2" />
          <span className="sk sk-3" />
        </div>
      </div>
    ));

  return (
    <div className="bc-page">
      <header className="bc-page-head">
        <span className="bc-eyebrow"><i />Cursos</span>
        <h1>Seus treinamentos</h1>
        <p>Organizados entre gratuitos e premium — continue de onde parou ou desbloqueie novos.</p>
      </header>

      {pageError ? <p className="bc-empty">{pageError}</p> : null}

      {loadingData ? (
        <div className="bc-grid">{renderSkeletons(6)}</div>
      ) : !sorted.length ? (
        <div className="bc-empty">Nenhum curso disponível ainda.</div>
      ) : (
        <>
          {free.length ? (
            <section className="bc-course-group">
              <div className="bc-group-head">
                <span className="bc-group-dot free" />
                <h2>Gratuitos</h2>
                <span className="bc-group-tag free">{free.length}</span>
              </div>
              <div className="bc-grid">{free.map(renderCard)}</div>
            </section>
          ) : null}

          {paid.length ? (
            <section className="bc-course-group">
              <div className="bc-group-head">
                <span className="bc-group-dot paid" />
                <h2>Premium</h2>
                <span className="bc-group-tag paid">{paid.length}</span>
              </div>
              <div className="bc-grid">{paid.map(renderCard)}</div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CoursesPage;
